import type { ReactNode } from 'react';
import { Link } from 'react-router';
import type { Page } from '@/api/types';
import { useFormFactor } from '@/lib/formFactor';
import { daysUntil, money, qty, shortDate } from '@/lib/format';
import { useSession } from '@/stores/session';
import { Tag } from '@/components/Tag';
import { Skeleton } from '@/components/States';
import { useResource } from '../inventory/api';
import { purchaseStatus } from '../inventory/status';
import type { PurchaseOrder, SuggestionPayload } from '../inventory/types';
import s from './WarehouseDashboard.module.css';

type ReadyPurchases = Page<PurchaseOrder> & { stats?: { pendingValue: number | string } };

function Task({ title, count, to, loading, error, empty, children }: {
  title: string; count?: number; to: string; loading: boolean; error?: Error; empty: string; children?: ReactNode;
}) {
  const hasChildren = Array.isArray(children) ? children.length > 0 : Boolean(children);
  return <section className={s.task}>
    <header className={s.taskHead}><h2>{title}</h2>{count !== undefined && <span className={s.count}>{count}</span>}<Link className={s.more} to={to}>全部 ›</Link></header>
    {loading && <div className={s.pad}><Skeleton lines={2} /></div>}
    {!loading && error && <p className={s.empty}>加载失败：{error.message}</p>}
    {!loading && !error && (hasChildren ? children : <p className={s.empty}>{empty}</p>)}
  </section>;
}

/** W1 的采购角色：建议与逾期队列按 V1 共用采购数据实时计算。 */
export function PurchaseDashboard() {
  const ff = useFormFactor();
  const user = useSession((state) => state.user);
  const can = useSession((state) => state.can);
  const canSuggest = can('purchase.suggestions.read');
  const canPurchase = can('purchase.read');
  const suggestions = useResource<SuggestionPayload>(canSuggest ? '/api/orders/purchase-suggestions' : null);
  const ready = useResource<ReadyPurchases>(canPurchase ? '/api/v2/purchase-orders/ready?pageSize=2' : null);
  const overdue = useResource<ReadyPurchases>(canPurchase ? '/api/v2/purchase-orders/ready?overdue=1&pageSize=3' : null);
  const now = new Date();
  const h = now.getHours();
  const greeting = h < 6 ? '夜深了' : h < 11 ? '早上好' : h < 13 ? '中午好' : h < 18 ? '下午好' : '晚上好';
  const tasksReady = suggestions.data && overdue.data;
  const todo = (suggestions.data?.purchaseSuggestionCount || 0) + (overdue.data?.pagination.total || 0);
  const reload = () => { suggestions.reload(); ready.reload(); overdue.reload(); };

  return <div className={`${s.page} ${s[ff]} ${s.purchase}`}>
    <header className={s.head}>
      <div><h1>{ff === 'phone' ? '主屏' : `${greeting}，${user?.name || '采购同事'}`}</h1>
        <p>{ff === 'phone' ? `${greeting}，${user?.name || '采购同事'} · ` : ''}{tasksReady ? <>今天有 <b>{todo}</b> 项采购任务待关注</> : '正在读取采购任务'} · 采购视角</p></div>
      <div className={s.headActions}><button type="button" onClick={reload}>刷新</button><Link className={s.primary} to="/purchase-suggestions">查看采购建议</Link></div>
    </header>
    <div className={s.scroll}>
      <div className={s.tiles}>
        <Link className={`${s.tile} ${s.blue}`} to="/purchase-suggestions"><span>待下单建议</span><b className="tnum">{suggestions.data?.purchaseSuggestionCount ?? '—'}</b></Link>
        <Link className={`${s.tile} ${s.red}`} to="/purchase-orders?overdue=1"><span>逾期未到货</span><b className="tnum">{overdue.data?.pagination.total ?? '—'}</b></Link>
        <Link className={s.tile} to="/purchase-orders"><span>待入库采购单</span><b className="tnum">{ready.data?.pagination.total ?? '—'}</b></Link>
        <Link className={s.tile} to="/purchase-orders"><span>在途货值</span><b className="tnum">{ready.data?.stats?.pendingValue !== undefined ? money(ready.data.stats.pendingValue) : '—'}</b></Link>
      </div>
      <div className={s.tasks}>
        {canSuggest && <Task title="待下单建议" count={suggestions.data?.purchaseSuggestionCount} to="/purchase-suggestions" loading={suggestions.loading} error={suggestions.error} empty="当前没有采购建议。">
          {suggestions.data?.purchaseSuggestions.slice(0, 3).map((item) => <div className={s.row} key={`${item.warehouseId}:${item.productId}:${item.supplierId || ''}:${item.packagingOptionId || ''}`}>
            <div className={s.rowMain}><b>{item.productName} <span className={s.muted}>{item.productCode}</span>{!item.supplierId && <Tag tone="amber" sm>缺供应商</Tag>}</b>
              <small>缺口 {qty(item.shortageQuantity)} · 建议采购 {qty(item.suggestedQuantity)} · {item.supplierName || '未设供应商'} · 关联 {item.orderCount} 张订单</small></div>
            <Link className={s.action} to={`/purchase-suggestions?q=${encodeURIComponent(item.productCode)}`}>去处理</Link>
          </div>)}
        </Task>}
        {canPurchase && <Task title="逾期未到货" count={overdue.data?.pagination.total} to="/purchase-orders?overdue=1" loading={overdue.loading} error={overdue.error} empty="没有逾期未到货的采购单。">
          {overdue.data?.items.map((po) => <div className={s.row} key={po.id}>
            <div className={s.rowMain}><b className="tnum">{po.purchaseNo} <Tag tone={purchaseStatus[po.status].tone} sm>{purchaseStatus[po.status].label}</Tag></b>
              <small>{po.supplier.name} · 预计 {shortDate(po.expectedArrivalDate)} · 已逾期 {-Math.min(daysUntil(po.expectedArrivalDate) || 0, 0)} 天</small></div>
            <Link className={s.action} to={`/purchase-orders/${po.id}`}>查看</Link>
          </div>)}
        </Task>}
        {canPurchase && <Task title="待入库采购单" count={ready.data?.pagination.total} to="/purchase-orders" loading={ready.loading} error={ready.error} empty="没有待入库采购单。">
          {ready.data?.items.map((po) => <div className={s.row} key={po.id}>
            <div className={s.rowMain}><b className="tnum">{po.purchaseNo} <Tag tone={purchaseStatus[po.status].tone} sm>{purchaseStatus[po.status].label}</Tag></b><small>{po.supplier.name} · {po.warehouse.name} · 预计 {shortDate(po.expectedArrivalDate)}</small></div>
            <Link className={s.action} to={`/purchase-orders/${po.id}`}>查看</Link>
          </div>)}
        </Task>}
      </div>
    </div>
  </div>;
}
